// src/utils/dreamInterpretation.js
import { GoogleGenerativeAI } from "@google/generative-ai";
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, BookOpen } from 'lucide-react';

// Existing code remains the same
// Safer API key retrieval
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY || process.env.VITE_GEMINI_API_KEY;

if (!API_KEY) {
  console.error('❌ No Gemini API Key found. Please check your environment configuration.');
}

// Initialize the Gemini AI client with error handling
const genAI = new GoogleGenerativeAI(API_KEY);

export const interpretDream = async (dreamContent) => {
  // Extensive logging and validation
  console.log('🔍 Interpreting Dream - Input:', {
    content: dreamContent,
    apiKeyPresent: !!API_KEY,
    type: typeof dreamContent,
    length: dreamContent ? dreamContent.length : 'N/A'
  });

  // Validate input with more precise checking
  if (!dreamContent) {
    console.error('❌ Dream content is undefined');
    throw new Error('Dream content is required for interpretation');
  }

  const trimmedContent = dreamContent.trim();
  if (trimmedContent === '') {
    console.error('❌ Dream content is an empty string after trimming');
    throw new Error('Dream content cannot be an empty string');
  }

  try {
    const model = genAI.getGenerativeModel({ model: "gemini-pro" });

    const prompt = `
      Provide a professional, insightful, and compassionate interpretation of the following dream:

      Dream Content:
      "${trimmedContent}"

      Please analyze the dream's symbolism, potential psychological meanings, and emotional significance. 
      Structure your response with:
      1. Key Symbols and Their Potential Meanings
      2. Emotional Landscape of the Dream
      3. Possible Psychological Insights
      4. Constructive Reflection

      Keep the interpretation empathetic, nuanced, and avoid overly generic statements.
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();

    // Log interpretation result
    console.log('✅ Dream Interpretation Result:', {
      length: text.length,
      truncatedPreview: text.slice(0, 100) + '...'
    });

    // Ensure non-empty interpretation
    const finalInterpretation = text.trim() || "The dream seems complex and defies simple interpretation. Each dream is unique to the dreamer's personal experience.";
    
    return finalInterpretation;
  } catch (error) {
    console.error('❌ Dream Interpretation Error:', {
      message: error.message,
      stack: error.stack,
      apiKeyLength: API_KEY ? API_KEY.length : 'N/A'
    });

    // More detailed error handling
    if (error.message.includes('API_KEY_INVALID')) {
      throw new Error('Invalid Gemini API Key. Please verify your configuration.');
    }

    throw error;
  }
};

export const generateDreamTitle = async (dreamDescription) => {
  // Validate input
  if (!dreamDescription || dreamDescription.trim() === '') {
    throw new Error('Dream description is required to generate a title');
  }

  try {
    const model = genAI.getGenerativeModel({ model: "gemini-pro" });

    const prompt = `
      Create a creative, evocative, and concise title for this dream that captures its essence:

      Dream Description:
      "${dreamDescription}"

      Guidelines:
      - Be imaginative and metaphorical
      - Reflect the core emotion or central image of the dream
      - Keep it under 10 words
      - Avoid literal descriptions
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();

    // Ensure non-empty title
    return text.trim() || "Echoes of the Subconscious";
  } catch (error) {
    console.error('Dream title generation error:', error);
    throw new Error('Failed to generate dream title. Please try again.');
  }
};